import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleet, type AssetControlView } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { Select, SearchInput } from "@/components/inputs";
import { ActionRow } from "@/components/ActionRow";
import { SoonButton } from "@/components/SoonButton";
import { DevicePicker } from "@/modules/asset_data/picker";
// THE VOCABULARY IS NOT COPIED HERE. Action numbers, level labels, reason
// codes and what each state MEANS all live in the catalogue, shared with the
// fleet-wide Commands page. Two copies is §9k's one-fact-two-homes bug, and
// in this subsystem a reason code spelled differently on two pages reads
// exactly like agreement until an operator acts on the wrong one.
import { ACT, ACT_LABEL, LEVEL_LABEL, COMMANDS, STATE_MEANS, reasonText,
         commandSpec, CommandStateChip as StateChip } from "@/modules/fleet/catalogue";

//: the entry this page's primary control is driven by. Non-null by
//: construction — the catalogue always declares it — and read once so a
//: rename there is a compile error here rather than an empty dropdown.
const IMMOB = commandSpec("immobilize")!;
import { Icon } from "@/components/icons";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago } from "@/lib/format";

/** Asset Control — send a command to one asset and see what happened to it.
 *
 *  THE PAGE'S ONE JOB IS TO NOT LIE ABOUT STATE, because three different
 *  things can look like "immobilised" and only one of them means the vehicle
 *  cannot move:
 *
 *    HOLD PLACED, NOT YET APPLIED  the device has the command and is waiting
 *                                  for the vehicle to halt. It is STILL MOVING
 *                                  and still mobile. Rendering this as
 *                                  "immobilised" is the error an operator would
 *                                  act on.
 *    APPLIED                       in force on the vehicle.
 *    HOLD PLACED, NOT ACTUATABLE   no connected part is installed, so the lien
 *                                  stands and nothing can reach the asset.
 *
 *  And the EFFECTIVE TYPE is shown with the level that decided it, because the
 *  highest open level dictates the type (OFL-06k): a company that asked for
 *  WHEELLOCK can be getting FREEWHEEL while a financier hold stands, and a bare
 *  "immobilised" would hide that from both of them. */

function Banner({ v }: { v: AssetControlView }) {
  const armed = v.commands.find((c) => c.state === "received");
  if (!v.immobile) {
    return (
      <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--ok)" }}>
        <b style={{ color: "var(--ok)" }}>MOBILE</b> — no holds on this asset.
      </div>
    );
  }
  // IMMOBILE per Orbit's holds. Now say which of the three it actually is.
  const applied = v.commands.some((c) => c.state === "applied");
  const colour = applied ? "var(--bad)" : "var(--warn)";
  return (
    <div className="panel panel-pad" style={{ borderLeft: `3px solid ${colour}` }}>
      <b style={{ color: colour }}>
        {applied ? "IMMOBILISED" : armed ? "ARMED — NOT YET APPLIED" : "HOLD PLACED"}
      </b>
      {" — "}
      {ACT_LABEL[v.effective_action]}
      {v.effective_level != null && (
        <> , decided by <b>{LEVEL_LABEL[v.effective_level] ?? `level ${v.effective_level}`}</b></>
      )}
      {!applied && armed && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          The device has the command and is waiting for the vehicle to come to a
          halt (controller data valid, speed and RPM zero, for 5 s).
          <b> The vehicle can still move until then.</b>
        </div>
      )}
      {!v.actuatable && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warn)" }}>
          No connected part is installed on this asset, so the hold is recorded
          but nothing can be sent to it.
        </div>
      )}
    </div>
  );
}

export function FleetControlPage() {
  const [params, setParams] = useSearchParams();
  const [assetId, setAssetId] = useState("");
  //: the picker's own selection. Asset Control acts on the ASSET, so this is
  //: only here to drive the picker and to warn when a search named a stream
  //: with no asset behind it.
  const [stream, setStream] = useState("");
  const [action, setAction] = useState<number>(ACT.FREEWHEEL);
  const [note, setNote] = useState("");
  const { can } = useSession();
  const qc = useQueryClient();
  const mayCommand = can(CAP.IMMOBILIZE);

  // `?asset=` is how the fleet map hands an asset over, the same shape the
  // Window and History pages use for `?imei=`.
  const deep = params.get("asset");
  useEffect(() => {
    if (!deep) return;
    setAssetId(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("asset"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["fleet", "asset", assetId],
    queryFn: () => fleet.asset(assetId),
    enabled: assetId.trim().length > 0,
    refetchInterval: POLL.STATE,
  });

  // STEP-UP. The server answers 428 STEP_UP_REQUIRED, which is a PROMPT and
  // not a failure — the client can fix it by asking for a code, which is
  // exactly why the status is 428 and not 403. Shown inline rather than as a
  // modal: an operator who has just typed an asset id should not lose it.
  const [needsStepUp, setNeedsStepUp] = useState(false);
  const [code, setCode] = useState("");
  const stepUp = useMutation({
    mutationFn: () => fleet.stepUp(code.trim()),
    onSuccess: () => { setNeedsStepUp(false); setCode(""); },
  });

  const onActError = (e: unknown) => {
    // 428 is the only error worth translating into UI state.
    if (String((e as Error)?.message ?? "").includes("STEP_UP_REQUIRED")) {
      setNeedsStepUp(true);
    }
  };

  const act = useMutation({
    mutationFn: (what: "immobilize" | "mobilize") =>
      what === "immobilize"
        ? fleet.immobilize(assetId, { action, note: note || undefined })
        : fleet.mobilize(assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "asset", assetId] }),
    onError: onActError,
  });

  // BULK LIVES ON THE FLEET-WIDE PAGE, NOT HERE. This page is about the one
  // asset in front of you; a form that can stop two hundred vehicles does not
  // belong on it. /fleet/commands owns that, with the batch list beside it.

  const v = q.data;

  return (
    <div className="mpage on">
      {/* THE PHRASE "the vehicle can still move" BELONGS TO THE BANNER ALONE,
          and is deliberately not repeated in this description. Two reasons,
          and the first is correctness: as static copy it would assert that an
          immobilised asset is mobile, which is only true while a command is
          armed and not yet applied. The second is that `container.textContent`
          concatenates the whole page, so a render test asserting the banner
          does NOT warn cannot tell the banner from a description — the same
          collision that made `/MOBILE/` unmatchable here once before. */}
      <PageHead eyebrow="Fleet · Asset Control"
        title="One asset, and what it has been told"
        desc={<>Immobilise or mobilise one asset, and see exactly what the
          device did with the command. A command is not in force until it says
          <b> applied</b> — one the device is merely holding has stopped
          nothing yet.</>}
        right={<Link className="btn ghost" to="/fleet/commands">Fleet commands</Link>} />

      {/* PANEL 1 — FIND THE ASSET. The same picker the Live Window and History
          use (company, search-by, asset type, search), because three
          hand-built selectors for one question is how they drift apart.
          Separate from the command panel at Dattatray's request: choosing what
          to act on and deciding what to do to it are different steps, and
          mixing them makes an accidental click easier. */}
      <div className="section-label flush">Find the asset</div>
      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={stream} onPick={setStream}
          onPickAsset={(a) => setAssetId(a ?? "")} />
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                      marginTop: 12, flexWrap: "wrap" }}>
          <div>
            <label className="simlab">Asset (VIN / BIN)</label>
            {/* Authoritative and editable: the picker writes into it, a deep
                link from the map fills it, and a VIN can still be pasted. */}
            <SearchInput mono style={{ width: 280 }} placeholder="DRILL-ASSET-001"
              value={assetId} onChange={(e) => setAssetId(e.target.value)} />
          </div>
          {stream && !assetId && (
            <span style={{ fontSize: 11.5, color: "var(--warn)", paddingBottom: 6 }}>
              That search names a data stream, not an asset. Search by Asset to
              place a hold — a hold belongs to the asset, not to the box.
            </span>
          )}
        </div>
      </div>

      {/* PANEL 2 — COMMAND IT. */}
      <div className="section-label">Command</div>
      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label className="simlab">Immobilise as</label>
            <Select style={{ width: 210 }} value={action}
              onChange={(e) => setAction(Number(e.target.value))}>
              {IMMOB.options.map((o) => (
                <option key={o.action} value={o.action}>{o.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="simlab">Note (optional)</label>
            <SearchInput style={{ width: 220 }} placeholder="why"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {/* The write controls sit behind CAP.IMMOBILIZE, not behind the module
            gate: opening Fleet is a read, stopping somebody's vehicle is not. */}
        <ActionRow>
          {!mayCommand && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginRight: "auto" }}>
              You can view an asset's state. Sending a command needs the
              immobilise permission.
            </span>
          )}
          {mayCommand && act.isError && (
            <span style={{ fontSize: 11.5, color: "var(--bad)", marginRight: "auto" }}>
              {String((act.error as Error)?.message ?? "command failed")}
            </span>
          )}
          <button className="btn ghost" disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("mobilize")}>
            <Icon name="ok" /> Mobilise
          </button>
          <button className="btn red" disabled={!assetId || !mayCommand || act.isPending}
            onClick={() => act.mutate("immobilize")}>
            <Icon name="blocked" /> Immobilise
          </button>
          {/* SHOWN AND DISABLED, not hidden. An operator wondering whether
              Orbit can cut the ignition should get an answer, and "soon" is
              an answer; a missing button is not. Driven by the catalogue's
              `built` flag, so these light up from one edit there. */}
          {COMMANDS.filter((c) => !c.built).map((c) => (
            <SoonButton key={c.key} why={`${c.label.toLowerCase()} — ${c.hint}`}>
              {c.label}
            </SoonButton>
          ))}
        </ActionRow>
      </div>

      {needsStepUp && (
        <div className="panel panel-pad" style={{ marginBottom: 14,
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Confirm it is you</b>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>
            Immobilising needs your authenticator code. It stays valid for five
            minutes, so a bulk run does not ask again per asset.
            {" "}<b>Releasing a vehicle never needs this.</b>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                        marginTop: 10 }}>
            <div>
              <label className="simlab">6-digit code or backup code</label>
              <SearchInput mono style={{ width: 180 }} value={code}
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)} />
            </div>
            <button className="btn teal" disabled={!code.trim() || stepUp.isPending}
              onClick={() => stepUp.mutate()}>Confirm</button>
            {stepUp.isError && (
              <span style={{ fontSize: 11.5, color: "var(--bad)" }}>
                That code was not accepted.
              </span>
            )}
          </div>
        </div>
      )}

      {!assetId && (
        <div className="panel panel-pad cap">
          Enter an asset, or click an asset on the Overview map and choose Asset
          Control.
        </div>
      )}
      {assetId && q.isError && (
        <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--bad)" }}>
          No such asset, or it is not yours.
        </div>
      )}

      {v && (
        <>
          <Banner v={v} />

          <div className="section-label tight">
            Holds — every one must clear before the asset is mobile
          </div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr>
                <th>Level</th><th>Type</th><th>Placed by</th><th>When</th><th>Note</th>
              </tr></thead>
              <tbody>
                {v.holds.length === 0 && (
                  <tr><td colSpan={5} className="cap">No holds.</td></tr>
                )}
                {v.holds.map((h) => (
                  <tr key={h.level}>
                    <td>{LEVEL_LABEL[h.level] ?? `level ${h.level}`}</td>
                    <td>{ACT_LABEL[h.action]}</td>
                    <td className="mono">{h.placed_by}</td>
                    <td>{h.placed_at ? `${ago(h.placed_at)} ago` : "—"}</td>
                    <td>{h.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {v.fota.length > 0 && (
            <>
              <div className="section-label tight">
                Firmware updates on this asset — read-only
              </div>
              <div className="tblwrap">
                <table className="tbl">
                  <thead><tr>
                    <th>Rollout</th><th>Target</th><th>Node</th><th>State</th>
                    <th>Attempts</th><th>Converged</th>
                  </tr></thead>
                  <tbody>
                    {v.fota.map((f, i) => (
                      <tr key={i}>
                        <td>{f.batch}</td>
                        <td className="num">v{f.target_version}</td>
                        <td>{f.software}</td>
                        <td>{f.state}</td>
                        <td className="num">{f.attempts}</td>
                        <td>{f.converged_at ? `${ago(f.converged_at)} ago` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
                Shown here so a command's behaviour makes sense — a device
                mid-rollout may be waiting for the same halt an immobilise is.
                Rollouts are started on the FOTA page.
              </div>
            </>
          )}

          <div className="section-label tight">Command log</div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr>
                <th>When</th><th>Level</th><th>Command</th><th>State</th>
                <th>Means</th><th>Reason</th><th>By</th>
              </tr></thead>
              <tbody>
                {v.commands.length === 0 && (
                  <tr><td colSpan={7} className="cap">Nothing sent yet.</td></tr>
                )}
                {v.commands.map((c) => (
                  <tr key={c.cmd_id}>
                    <td>{c.created_at ? `${ago(c.created_at)} ago` : "—"}</td>
                    <td>{c.level != null ? (LEVEL_LABEL[c.level] ?? c.level) : "—"}</td>
                    <td>{ACT_LABEL[c.action] ?? c.action}</td>
                    <td><StateChip c={c} /></td>
                    <td style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</td>
                    <td>{reasonText(c.reason_code)}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{c.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

    </div>
  );
}
